import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Menu } from 'antd';
import {
  ClusterOutlined,
  UnorderedListOutlined,
  TableOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Link, Redirect, Route, Switch } from 'react-router-dom';
import { getTournament, getGroups } from 'api/events';
import InnerAppLayout from 'componentsv2/InnerLayout/InnerAppLayout';
import Groups from './Groups';
import Participants from './Participants';
import Stadistics from './Stadistics';
import Information from './Information';
import useNetworkState from 'hooks/useNetworkState';
import BlockPageLoader from 'components/Loaders/BlockPageLoader';

export const EventContext = createContext({ eventId: null, event: null });

const SettingOption = ({ match, location }) => {
  return (
    <Menu
      defaultSelectedKeys={`${match.url}/grupos`}
      mode="inline"
      selectedKeys={[location.pathname]}
    >
      <Menu.Item key={`${match.url}/grupos`}>
        <ClusterOutlined />
        <span>Grupos</span>
        <Link to={'grupos'} />
      </Menu.Item>
      <Menu.Item key={`${match.url}/participantes`}>
        <UnorderedListOutlined />
        <span>Participantes</span>
        <Link to={'participantes'} />
      </Menu.Item>
      <Menu.Item key={`${match.url}/estadisticas`}>
        <TableOutlined />
        <span>Estadísticas</span>
        <Link to={`estadisticas`} />
      </Menu.Item>
      <Menu.Item key={`${match.url}/informacion`}>
        <InfoCircleOutlined />
        <span>Información</span>
        <Link to={`informacion`} />
      </Menu.Item>
    </Menu>
  );
};

const SettingContent = ({ match }) => {
  return (
    <Switch>
      <Redirect exact from={`${match.url}`} to={`${match.url}/grupos`} />
      <Route path={`${match.url}/grupos`} component={Groups} />
      <Route path={`${match.url}/participantes`} component={Participants} />
      <Route path={`${match.url}/estadisticas`} component={Stadistics} />
      <Route path={`${match.url}/informacion`} component={Information} />
    </Switch>
  );
};

export const Event = props => {
  const eventId = props?.match?.params?.eventId;
  const backgroundFetchNetworkState = useNetworkState();

  const resolver = useCallback(
    async (key, _eventId) => {
      backgroundFetchNetworkState.setIsLoading();
      const { body } = await getGroups(_eventId);
      backgroundFetchNetworkState.setDoneLoading();

      return body?.data?.groups || [];
    },
    [backgroundFetchNetworkState],
  );

  const { data, refetch: reloadGroups } = useQuery(['event-groups', eventId], resolver);
  const groups = useMemo(() => data || [], [data]);

  const eventResolver = useCallback(async (key, _eventId) => {
    const { body, status } = await getTournament(_eventId);

    return { event: body?.data?.tournament, status } || null;
  }, []);

  const { data: eventData, refetch: reloadEvent, status } = useQuery(
    ['event', eventId],
    eventResolver,
  );
  const event = useMemo(() => eventData?.event || null, [eventData]);
  const notFound = useMemo(() => eventData?.status === 404, [eventData]);
  const isLoading = useMemo(() => status === 'loading', [status]);

  useEffect(() => {
    reloadEvent();
  }, [eventId]); // eslint-disable-line

  useEffect(() => {
    reloadGroups();
  }, [event]); // eslint-disable-line

  if (isLoading) {
    return <BlockPageLoader className="min-h-screen" valignTop />;
  }

  if (notFound) {
    return (
      <div className="py-12 px-10" style={{ minHeight: '80vh' }}>
        <h1 className="text-3xl text-brandblue font-roboto font-bold">Algo salio mal.</h1>
        <p>No se pudo cargar la informacion solicitada. El evento parece no existir.</p>
      </div>
    );
  }

  return (
    <EventContext.Provider
      value={{
        eventId,
        event,
        isLoading,
        groups,
        reloadGroups,
        reloadEvent,
        isBackgroundFetchLoading: backgroundFetchNetworkState.isLoading,
      }}
    >
      <InnerAppLayout
        sideContentWidth={320}
        sideContent={<SettingOption {...props} />}
        mainContent={<SettingContent {...props} />}
      />
    </EventContext.Provider>
  );
};

export default Event;
